import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import BulkOrderRequestForm from "../components/BulkOrderRequestForm";
import {
  ShoppingBag,
  UsersThree,
  BookOpen,
  Sparkle,
  EnvelopeSimple,
  ArrowRight,
  ChatCircleText,
  Handshake,
  CheckCircle,
} from "@phosphor-icons/react";

const AUDIENCES = [
  "Churches",
  "Hospice Organizations",
  "Senior Living Communities",
  "Healthcare Organizations",
  "Home Health Agencies",
  "Community Organizations",
  "Educational Institutions",
  "Nonprofit Organizations",
  "Caregiver Support Groups",
];

const AVAILABLE_RESOURCES = [
  "From Diagnosis to Goodbye",
  "From Diagnosis to Goodbye Hospice Workbook",
  "From Diagnosis to Goodbye Bereavement Workbook",
];

const IN_DEVELOPMENT_RESOURCES = [
  "From Diagnosis to Goodbye Scripture Companion",
  "Facilitator Guides",
  "From Diagnosis to Goodbye Participant Guide",
  "Licensing Handbook",
  "Training Materials",
];

const USE_CASES = [
  "Church small-group studies",
  "Hospice staff education",
  "Volunteer training",
  "Caregiver workshops",
  "Community seminars",
  "Conferences",
  "Ministry events",
  "Educational programs",
];

const BulkOrders = () => {
  return (
    <div data-testid="page-bulk-orders">
      <Seo
        title="Bulk Orders for Churches, Hospices & Ministries"
        description="Order books, workbooks, and grief resources in bulk for churches, hospices, chaplaincies, and care ministries. Special pricing and quote requests welcome."
      />
      {/* HERO */}
      <section
        data-testid="bulk-hero"
        className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-br from-softblue/60 via-white to-secondary/5"
        />
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-secondary/10 blur-3xl -z-10"
        />
        <div className="container-x max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            Bulk Orders
          </span>
          <h1
            data-testid="bulk-hero-title"
            className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900"
          >
            Equip Your Organization with Compassionate Resources
          </h1>
          <p
            data-testid="bulk-hero-intro"
            className="mt-6 text-ink-700 text-lg leading-relaxed"
          >
            Whether you&rsquo;re ordering books for a church study, hospice
            staff, volunteer training, caregiver support group, community
            seminar, or educational event, Begin Again Living Ministries is
            available to help your organization find the best purchasing
            solution.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/bulk-orders#request-quote"
              data-testid="bulk-hero-quote-btn"
              aria-label="Request a bulk order quote from Begin Again Living Ministries"
              className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
            >
              <ShoppingBag size={18} weight="duotone" />
              Request a Bulk Order Quote
            </Link>
            <Link
              to="/contact"
              data-testid="bulk-hero-contact-btn"
              className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-7 py-3.5 font-semibold hover:bg-primary/5 transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
            >
              <EnvelopeSimple size={18} weight="duotone" />
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-16 border-t border-ink-100">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Who We Serve
            </span>
            <div className="mt-5 flex items-start gap-4">
              <span className="hidden sm:inline-flex items-center justify-center w-14 h-14 shrink-0 rounded-2xl bg-primary/8 text-primary border border-primary/10">
                <UsersThree size={26} weight="duotone" />
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
                Bulk ordering is available for:
              </h2>
            </div>
          </div>
          <ul
            data-testid="bulk-audiences"
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            {AUDIENCES.map((a) => (
              <li
                key={a}
                className="flex items-center gap-3 bg-white border border-ink-100 rounded-2xl px-5 py-4 text-ink-800"
              >
                <CheckCircle size={18} weight="duotone" className="text-secondary shrink-0" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* AVAILABLE RESOURCES */}
      <section className="py-16 border-t border-ink-100">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Available for Bulk Order Requests
            </span>
            <div className="mt-5 flex items-start gap-4">
              <span className="hidden sm:inline-flex items-center justify-center w-14 h-14 shrink-0 rounded-2xl bg-primary/8 text-primary border border-primary/10">
                <BookOpen size={26} weight="duotone" />
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
                Organizations may purchase multiple copies of:
              </h2>
            </div>
          </div>
          <ul
            data-testid="bulk-resources"
            className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {AVAILABLE_RESOURCES.map((title) => (
              <li
                key={title}
                data-testid="bulk-resource-available"
                className="flex items-center justify-between gap-3 rounded-2xl px-5 py-5 border bg-white border-primary/25 shadow-[0_10px_30px_-14px_rgba(59,110,143,0.35)]"
              >
                <div className="flex-1">
                  <p className="font-heading text-lg text-ink-900">{title}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
                    Available Now
                  </p>
                </div>
                <CheckCircle
                  size={22}
                  weight="duotone"
                  className="text-primary shrink-0"
                />
              </li>
            ))}
          </ul>

          {/* IN-DEVELOPMENT — visually secondary */}
          <div className="mt-14" data-testid="bulk-in-development">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-ink-500 font-semibold">
                <Sparkle size={12} weight="duotone" className="text-secondary" />
                Additional Resources in Development
              </span>
              <p className="mt-3 text-ink-600 text-sm leading-relaxed">
                Additional organizational resources are in development and will
                be added to Bulk Order options as they become available.
              </p>
            </div>
            <ul
              data-testid="bulk-in-development-list"
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            >
              {IN_DEVELOPMENT_RESOURCES.map((title) => (
                <li
                  key={title}
                  className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 border border-dashed border-ink-200 bg-transparent"
                >
                  <span className="text-sm text-ink-600">{title}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-semibold shrink-0">
                    In Development
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHY BULK ORDERS + USE CASES */}
      <section className="py-16 border-t border-ink-100">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Why Bulk Orders?
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
              Make it easier to equip everyone in the room.
            </h2>
            <p className="mt-5 text-ink-700 text-lg leading-relaxed">
              Whether an organization is training staff, leading a church
              class, supporting caregivers, or providing educational
              resources to families, bulk ordering makes it easier to
              provide participants with the materials they need.
            </p>
            <p className="mt-4 text-ink-700 leading-relaxed">Bulk orders may be appropriate for:</p>
          </div>
          <ul
            data-testid="bulk-use-cases"
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
          >
            {USE_CASES.map((u) => (
              <li
                key={u}
                className="flex items-center gap-3 bg-white border border-ink-100 rounded-2xl px-5 py-4 text-ink-800"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-secondary shrink-0" aria-hidden="true" />
                <span>{u}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* VOLUME PRICING + PERSONALIZED ASSISTANCE */}
      <section className="py-16 border-t border-ink-100 bg-softblue/40">
        <div className="container-x grid md:grid-cols-2 gap-8 lg:gap-12">
          <div
            data-testid="bulk-volume-pricing"
            className="bg-white border border-ink-100 rounded-3xl p-8 sm:p-10 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Volume Pricing
            </span>
            <h3 className="mt-4 font-heading text-2xl sm:text-3xl text-ink-900 font-medium leading-snug">
              Customized quotes for every organization.
            </h3>
            <p className="mt-5 text-ink-700 leading-relaxed">
              Organizations purchasing multiple copies may qualify for volume
              pricing based on the products and quantities requested. Submit a
              Bulk Order Quote Request below and Begin Again Living Ministries
              will follow up with pricing, availability, shipping, and next
              steps.
            </p>
          </div>

          <div
            data-testid="bulk-personalized-assistance"
            className="bg-white border border-ink-100 rounded-3xl p-8 sm:p-10 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <Handshake size={14} weight="duotone" className="text-secondary" />
              Personalized Assistance
            </span>
            <h3 className="mt-4 font-heading text-2xl sm:text-3xl text-ink-900 font-medium leading-snug">
              Every organization has different goals.
            </h3>
            <p className="mt-5 text-ink-700 leading-relaxed">
              Begin Again Living Ministries can help organizations determine
              the right combination of currently available books and guided
              workbooks for staff education, caregiver support, family
              programs, church ministries, workshops, and community events. As
              additional facilitator, participant, training, and licensing
              resources become available, those options will also be added.
            </p>
          </div>
        </div>
      </section>

      {/* REQUEST A QUOTE — inline structured form */}
      <section
        id="request-quote"
        data-testid="bulk-request-quote"
        className="py-20 sm:py-24 border-y border-ink-100 scroll-mt-24 bg-paper/40"
      >
        <div className="container-x max-w-3xl">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Request a Quote
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.15]">
              Ready to equip your organization?
            </h2>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed">
              Share a few details about your organization and needs, and
              we&rsquo;ll follow up with a personalized proposal.
            </p>
          </div>
          <div className="mt-10">
            <BulkOrderRequestForm />
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/contact"
              data-testid="bulk-request-contact-btn"
              className="inline-flex items-center gap-2 text-sm text-primary font-semibold hover:text-primary-dark"
            >
              <ChatCircleText size={16} weight="duotone" />
              Have a question first? Contact us
            </Link>
          </div>
        </div>
      </section>

      {/* FUTURE NOTE */}
      <section className="py-14 sm:py-16">
        <div className="container-x max-w-3xl">
          <p
            data-testid="bulk-future-note"
            className="text-sm text-ink-500 italic leading-relaxed text-center"
          >
            As the From Diagnosis to Goodbye educational platform
            continues to grow, additional organizational purchasing options,
            digital resources, facilitator materials, and licensing
            opportunities will become available.
          </p>
        </div>
      </section>
    </div>
  );
};

export default BulkOrders;
